import { UseChatsResult } from '../../hooks/useChats';
import { UseVersionsResult } from '../../hooks/useVersions';
import ChatComposer from './ChatComposer';
import ChatMessageList from './ChatMessageList';
import ChatSelector from './ChatSelector';
import { IconClock } from '../Icons';
import { formatDate, formatDateTime } from '../../utils/relativeTime';

interface ChatPanelProps {
  chats: UseChatsResult;
  onOpenDrawer: () => void;
  onCancel: () => void;
  isCancelling: boolean;
  /** The project's values are being arranged into panels. */
  organizing: boolean;
  hideSelector?: boolean;
  userName?: string;
  projectId?: string;
  versions?: UseVersionsResult;
}

export default function ChatPanel({
  chats,
  onOpenDrawer,
  onCancel,
  isCancelling,
  organizing,
  hideSelector,
  userName,
  projectId,
  versions,
}: ChatPanelProps) {
  const lastVersionId = chats.currentChat?.lastVersionId ?? null;
  const activeVersionId = versions?.activeVersionId ?? null;
  const showVersionMismatch =
    !!lastVersionId && !!activeVersionId && lastVersionId !== activeVersionId;
  const activeSemver = versions?.versions.find((v) => v.isActive)?.semver;
  const chatSemver = versions?.versions.find((v) => v.id === lastVersionId)?.semver;
  const isSwitching = !!lastVersionId && versions?.busyVersionId === lastVersionId;
  const startedOn = formatDate(chats.currentChat?.createdAt);
  const startedAt = formatDateTime(chats.currentChat?.createdAt);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div
        style={{
          padding: '16px 24px 8px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {hideSelector ? (
          <>
            <span className="eyebrow">
              {chats.isDraftMode
                ? 'New chat'
                : chats.currentChat?.name ?? 'Chat'}
            </span>
            {startedOn && (
              <span className="muted-2" style={{ fontSize: 12 }} title={startedAt ?? undefined}>
                · started {startedOn}
              </span>
            )}
          </>
        ) : (
          <ChatSelector
            chats={chats.chats}
            currentChatId={chats.currentChatId}
            isDraftMode={chats.isDraftMode}
            onSelect={chats.selectChat}
            onBeginDraft={chats.beginDraftChat}
          />
        )}
        <div style={{ flex: 1 }} />
        <button type="button" onClick={onOpenDrawer} className="btn ghost sm">
          <IconClock size={14} />
          Detail
        </button>
      </div>

      {showVersionMismatch && (
        <div
          style={{
            margin: '0 24px 8px',
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            fontSize: 12.5,
            color: 'var(--text-2)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ flex: 1 }}>
            Your game is on{activeSemver ? ` v${activeSemver}` : ' a newer version'}, but this chat last
            edited{chatSemver ? ` v${chatSemver}` : ' an earlier version'}. New messages will build on the
            current version.
          </span>
          {lastVersionId && versions && (
            <button
              type="button"
              className="btn sm"
              disabled={isSwitching}
              onClick={() => versions.activate(lastVersionId)}
              title="Switch the game back to this chat's version"
            >
              {isSwitching ? 'Switching…' : `Switch to${chatSemver ? ` v${chatSemver}` : ' that version'}`}
            </button>
          )}
        </div>
      )}

      <ChatMessageList
        messages={chats.currentMessages}
        milestones={chats.currentMilestones}
        isLoading={chats.isLoadingMessages}
        isDraftMode={chats.isDraftMode}
        runningJobId={chats.currentChat?.currentJobId ?? null}
        organizing={organizing}
        onCancel={onCancel}
        isCancelling={isCancelling}
        userName={userName}
        projectId={projectId}
      />

      <ChatComposer
        onSend={chats.sendMessage}
        isAnyRunning={chats.isAnyRunning}
        isCurrentRunning={chats.isCurrentRunning}
        organizing={organizing}
        sendError={chats.sendError}
        projectId={projectId}
        chatId={chats.currentChatId ?? undefined}
      />
    </div>
  );
}
